import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2, ImagePlus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../lib/utils';
import { navigateWithInferredCategory } from '../lib/searchNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 6 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

/** Logica condivisa: upload foto → identificazione backend → pagina risultati. */
export const useImageSearch = ({ testId = 'image-search-input', capture = true } = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Solo utenti registrati: ogni foto costa una chiamata AI (anti-abuso)
  const requireLogin = useCallback(() => {
    if (user) return true;
    toast.error(t('photoSearchLoginRequired'));
    document.querySelector('[data-testid="header-login-btn"]')?.click();
    return false;
  }, [user, t]);

  const submitFile = useCallback(async (file) => {
    if (!file) return;
    if (!requireLogin()) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error(t('imageSearchFormatError'));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t('imageSearchTooLarge'));
      return;
    }

    setLoading(true);
    const toastId = toast.loading(t('imageSearchAnalyzing'));
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('lang', i18n.language || 'de');
      const res = await fetch(`${BACKEND_URL}/api/search-by-image`, {
        method: 'POST', body, credentials: 'include', headers: { ...authHeaders() },
      });
      const data = await res.json();
      if (res.status === 401) {
        toast.error(t('photoSearchLoginRequired'), { id: toastId });
        document.querySelector('[data-testid="header-login-btn"]')?.click();
        return;
      }
      if (!res.ok || !data.ok || !data.query) {
        toast.error(t('imageSearchNotRecognized'), { id: toastId });
        return;
      }
      toast.success(`${t('imageSearchFound')}: ${data.label || data.query}`, { id: toastId });
      navigateWithInferredCategory(navigate, data.query, { extraParams: { from: 'photo' } });
    } catch {
      toast.error(t('imageSearchError'), { id: toastId });
    } finally {
      setLoading(false);
    }
  }, [i18n.language, navigate, requireLogin, t]);

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      {...(capture ? { capture: 'environment' } : {})}
      className="hidden"
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; submitFile(f); }}
      data-testid={testId}
    />
  );

  return { loading, input, submitFile,
           open: () => { if (requireLogin()) inputRef.current?.click(); } };
};

/** Icona fotocamera compatta (header / barra di ricerca). */
export const ImageSearchButton = ({ className = '', size = 15 }) => {
  const { t } = useTranslation();
  const { loading, input, open } = useImageSearch();

  return (
    <>
      {input}
      <button
        type="button"
        onClick={open}
        disabled={loading}
        title={t('imageSearchTitle')}
        aria-label={t('imageSearchTitle')}
        data-testid="image-search-button"
        className={`text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 ${className}`}
      >
        {loading
          ? <Loader2 size={size} className="animate-spin" />
          : <Camera size={size} />}
      </button>
    </>
  );
};

/** Riquadro in evidenza per la homepage: "Cerca con una foto". */
export const PhotoSearchCard = () => {
  const { t } = useTranslation();
  const { loading, input, open, submitFile } = useImageSearch({ testId: 'photo-search-card-input', capture: false });
  const [dragging, setDragging] = useState(false);

  return (
    <div className="max-w-2xl mx-auto">
      {input}
      <button
        type="button"
        onClick={open}
        disabled={loading}
        data-testid="photo-search-card"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!loading) submitFile(e.dataTransfer.files?.[0]);
        }}
        className={`group w-full flex items-center gap-3 sm:gap-4 text-left rounded-2xl border px-4 py-3 sm:px-5 sm:py-4 transition-colors disabled:opacity-60
          ${dragging
            ? 'border-violet-400 bg-violet-500/10'
            : 'border-violet-300/60 dark:border-violet-500/25 bg-violet-500/[0.06] hover:bg-violet-500/[0.11]'}`}
      >
        <span className="shrink-0 grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300 transition-transform group-hover:-rotate-6">
          {loading
            ? <Loader2 size={20} className="animate-spin" />
            : <ImagePlus size={20} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="font-semibold text-sm sm:text-base">{t('photoSearchCardTitle')}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500 text-white">
              {t('photoSearchCardBadge')}
            </span>
          </span>
          <span className="block text-xs sm:text-sm text-muted-foreground mt-0.5">
            {loading ? t('imageSearchAnalyzing') : t('photoSearchCardDesc')}
          </span>
        </span>
        <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
          <span className="hidden sm:inline">{t('photoSearchCardCta')}</span>
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>
    </div>
  );
};
